<script setup lang="ts">
import ImageChevronIcon from './ImageChevronIcon.vue'

interface Option {
  value: string
  label: string
  description?: string
  tags?: string[]
  disabled?: boolean
}

withDefaults(defineProps<{
  modelValue: string
  label: string
  options: Option[]
  disabled?: boolean
  searchable?: boolean
}>(), { disabled: false, searchable: false })

const emit = defineEmits<{ (event: 'update:modelValue', value: string): void }>()
</script>

<template>
  <div class="image-field">
    <span class="image-field-label">{{ label }}</span>
    <el-select
      :model-value="modelValue"
      :empty-values="[null, undefined]"
      :aria-label="label"
      :disabled="disabled"
      :filterable="searchable"
      :suffix-icon="ImageChevronIcon"
      :placeholder="$t(searchable ? 'enterprise_image_search_languages' : 'image2_no_options')"
      :no-data-text="$t('image2_no_options')"
      :no-match-text="$t('image2_no_options')"
      popper-class="enterprise-image-select-menu"
      @update:model-value="emit('update:modelValue', String($event))"
    >
      <el-option v-for="option in options" :key="option.value" :value="option.value" :label="option.label" :disabled="option.disabled">
        <el-tooltip :disabled="!option.description" :content="option.description" placement="right" :show-after="150" popper-class="enterprise-image-option-tip">
          <span class="image-option-content">
            <span>{{ option.label }}</span>
            <span v-for="tag in option.tags" :key="tag" class="image-option-tag">{{ tag }}</span>
          </span>
        </el-tooltip>
      </el-option>
    </el-select>
  </div>
</template>

<style scoped lang="scss">
.image-field { display: flex; flex-direction: column; min-width: 0; gap: 6px; }
.image-field-label { color: #8f959e; font-size: 11.5px; line-height: 17px; }
.image-field :deep(.el-select) { width: 100%; --el-select-border-color-hover: #2b7de9; --el-select-input-focus-border-color: #2b7de9; }
.image-field :deep(.el-select__wrapper) { min-height: 40px; border-radius: 8px; padding: 0 12px; box-shadow: 0 0 0 1px #d9dee5 inset; background: #fff; font-size: 13px; }
.image-field :deep(.el-select__selected-item) { color: #1f2329; }
.image-field :deep(.el-select__placeholder.is-transparent) { color: #8f959e; }
.image-field :deep(.el-select__wrapper.is-disabled) { background: #f7f8fa; cursor: not-allowed; }
.image-field :deep(.el-select__wrapper.is-disabled .el-select__selected-item) { color: #a6adb7; }
.image-field :deep(.el-input__wrapper) { height: 40px; box-sizing: border-box; border-radius: 8px; padding: 0 12px; }
.image-field :deep(.el-input__inner) { font-size: 13px; }
.image-option-content { display: flex; align-items: center; gap: 6px; width: 100%; min-height: 34px; }
.image-option-tag { border-radius: 4px; padding: 0 5px; background: #fff0ee; color: #f36a57; font-size: 11px; line-height: 18px; }
</style>

<style lang="scss">
.enterprise-image-select-menu { max-width: calc(100vw - 32px); border-radius: 10px !important; }
.enterprise-image-select-menu .el-select-dropdown__item { height: auto; min-height: 34px; line-height: 20px; padding: 0 12px; font-size: 13px; white-space: normal; }
.enterprise-image-select-menu .el-select-dropdown__item.is-selected { color: #2b7de9; background: #f0f6ff; }
.enterprise-image-option-tip { max-width: min(340px, calc(100vw - 32px)); line-height: 1.6; }
</style>
